// Step-based deterministic simulation (§2.3, §2.4, §2.7, §2.8).
// Every numeric field of SimState stays a 32-bit integer: `| 0` / Math.imul only.

import {
  BAR_BOTTOM,
  BAR_TOP,
  COYOTE_FRAMES,
  FALL_DEATH_Y,
  FAST_FALL_GRAVITY,
  GRAVITY,
  HITBOX_SLIDE_H,
  HITBOX_SLIDE_W,
  HITBOX_STAND_H,
  HITBOX_STAND_W,
  INPUT_BUFFER_FRAMES,
  JUMP_VELOCITY,
  MAX_FRAMES,
  MAX_REVIVES,
  PLAYER_START_X,
  REVIVE_INVULN_FRAMES,
  SLIDE_MAX_FRAMES,
  SPIKE_H,
} from './constants';
import { speedAtFrame } from './distance';
import { generateTrack } from './track';
import { Action, type InputEvent, type Obstacle, type SimState } from './types';

const NO_EVENTS: readonly InputEvent[] = [];

export function initialState(): SimState {
  return {
    frame: 0,
    x: PLAYER_START_X,
    y: 0,
    vy: 0,
    sliding: false,
    slideFrames: 0,
    grounded: true,
    dead: false,
    finished: false,
    deathFrame: -1,
    revivesUsed: 0,
    invulnFrames: 0,
  };
}

export class Sim {
  readonly seed: number;
  readonly obstacles: Obstacle[];

  private s: SimState;
  /** Remaining coyote frames; > 0 means a jump is still allowed while airborne. */
  private coyote = 0;
  /** Remaining frames a pressed-but-unusable jump stays buffered. */
  private buffer = 0;
  /** SlideStart received and not yet released by SlideEnd. */
  private slideHeld = false;
  /** Slide pressed while airborne → gravity ×3 until landing / SlideEnd. */
  private fastFall = false;
  /** Index of the first obstacle that can still matter (obstacles are x-sorted). */
  private cursor = 0;

  /**
   * `gen` = the track's persisted sim_version (§4bis.24); defaults to the
   * current SIM_VERSION. Ignored when `obstacles` is passed explicitly.
   */
  constructor(seed: number, obstacles?: Obstacle[], gen?: string) {
    this.seed = seed | 0;
    this.obstacles = obstacles ?? generateTrack(this.seed, gen);
    this.s = initialState();
  }

  get state(): Readonly<SimState> {
    return this.s;
  }

  /** Current hitbox width, FQ. */
  get hitW(): number {
    return this.s.sliding ? HITBOX_SLIDE_W : HITBOX_STAND_W;
  }

  /** Current hitbox height, FQ. */
  get hitH(): number {
    return this.s.sliding ? HITBOX_SLIDE_H : HITBOX_STAND_H;
  }

  /** Simulate one frame using the events recorded for that frame. */
  step(events: readonly InputEvent[] = NO_EVENTS): void {
    const s = this.s;
    if (s.finished) return;

    if (s.dead) {
      // A revive may still arrive in a later step call; it is only accepted when
      // the event's frame matches the reported death frame.
      this.applyRevives(events);
      if (s.dead) return;
    }

    const f = s.frame;

    for (let i = 0; i < events.length; i++) {
      const e = events[i]!;
      if (e.a === Action.Revive) continue;
      this.applyInput(e.a);
    }

    // --- horizontal ----------------------------------------------------------
    s.x = (s.x + speedAtFrame(f)) | 0;
    this.advanceCursor();

    // --- ground support ------------------------------------------------------
    // While invulnerable after a revive the player is never dropped by a gap,
    // otherwise a revive over a hole would be unwinnable.
    const supported = s.invulnFrames > 0 ? true : !this.overGap(s.x, this.hitW);

    // --- vertical ------------------------------------------------------------
    let coyoteJustArmed = false;
    if (s.grounded && !supported) {
      s.grounded = false;
      s.vy = 0;
      this.coyote = COYOTE_FRAMES;
      coyoteJustArmed = true;
    }

    if (!s.grounded) {
      const g = this.fastFall ? FAST_FALL_GRAVITY : GRAVITY;
      const prevY = s.y;
      s.y = (s.y + s.vy) | 0;
      s.vy = (s.vy - g) | 0;
      // Landing requires crossing the ground plane from above: once the player
      // has dropped below y = 0 inside a gap they are past the ledge and keep
      // falling even after the gap ends.
      if (s.y <= 0 && supported && prevY >= 0) {
        s.y = 0;
        s.vy = 0;
        s.grounded = true;
        this.coyote = 0;
        this.fastFall = false;
        if (this.buffer > 0) {
          this.doJump(); // §2.7 buffered jump fires on landing
        } else if (this.slideHeld && !s.sliding) {
          s.sliding = true; // §2.7 airborne SlideStart auto-slides on landing
          s.slideFrames = 0;
        }
      } else if (!coyoteJustArmed && this.coyote > 0) {
        this.coyote = (this.coyote - 1) | 0;
      }
    } else {
      s.y = 0;
      s.vy = 0;
    }

    if (this.buffer > 0) this.buffer = (this.buffer - 1) | 0;

    // --- slide timer ---------------------------------------------------------
    if (s.sliding) {
      s.slideFrames = (s.slideFrames + 1) | 0;
      if (s.slideFrames >= SLIDE_MAX_FRAMES) {
        s.sliding = false;
        s.slideFrames = 0;
      }
    }

    // --- death, evaluated after integrating position -------------------------
    if (s.y < FALL_DEATH_Y) {
      this.die(f);
    } else if (s.invulnFrames === 0 && this.hits(s.x, s.y, this.hitW, this.hitH)) {
      this.die(f);
    }

    if (s.invulnFrames > 0) s.invulnFrames = (s.invulnFrames - 1) | 0;

    s.frame = (f + 1) | 0;
    if (!s.dead && s.frame >= MAX_FRAMES) s.finished = true;

    // A same-frame revive resumes the run immediately.
    this.applyRevives(events);
  }

  clone(): Sim {
    const c = new Sim(this.seed, this.obstacles);
    c.s = { ...this.s };
    c.coyote = this.coyote;
    c.buffer = this.buffer;
    c.slideHeld = this.slideHeld;
    c.fastFall = this.fastFall;
    c.cursor = this.cursor;
    return c;
  }

  // --- internals -------------------------------------------------------------

  private applyInput(a: Action): void {
    const s = this.s;
    switch (a) {
      case Action.Jump: {
        if (s.grounded) this.doJump();
        else if (this.coyote > 0) this.doJump();
        else this.buffer = INPUT_BUFFER_FRAMES;
        break;
      }
      case Action.SlideStart: {
        this.slideHeld = true;
        if (s.grounded) {
          s.sliding = true;
          s.slideFrames = 0;
          this.fastFall = false;
        } else {
          // Slam (sim v3): a slide command cancels any remaining ascent so the
          // swipe-down reaches the ground in a few frames instead of waiting
          // ~10 frames for jump velocity to bleed off — phone playtests showed
          // the old chain (jump rise → fast-fall → land → slide) was fatal at
          // late-game speed.
          this.fastFall = true;
          if (s.vy > 0) s.vy = 0;
        }
        break;
      }
      case Action.SlideEnd: {
        this.slideHeld = false;
        this.fastFall = false;
        if (s.sliding) {
          s.sliding = false;
          s.slideFrames = 0;
        }
        break;
      }
      default:
        break;
    }
  }

  private doJump(): void {
    const s = this.s;
    s.vy = JUMP_VELOCITY;
    s.grounded = false;
    s.sliding = false;
    s.slideFrames = 0;
    this.coyote = 0;
    this.buffer = 0;
    this.slideHeld = false;
    this.fastFall = false;
  }

  private die(frame: number): void {
    const s = this.s;
    s.dead = true;
    s.deathFrame = frame | 0;
  }

  private applyRevives(events: readonly InputEvent[]): void {
    const s = this.s;
    for (let i = 0; i < events.length; i++) {
      const e = events[i]!;
      if (e.a !== Action.Revive) continue;
      if (!s.dead) continue;
      if (e.f !== s.deathFrame) continue; // §2.7: only at the reported death frame
      if (s.revivesUsed >= MAX_REVIVES) continue;
      s.dead = false;
      s.deathFrame = -1;
      s.y = 0;
      s.vy = 0;
      s.grounded = true;
      s.sliding = false;
      s.slideFrames = 0;
      s.revivesUsed = (s.revivesUsed + 1) | 0;
      s.invulnFrames = REVIVE_INVULN_FRAMES;
      this.coyote = 0;
      this.buffer = 0;
      this.slideHeld = false;
      this.fastFall = false;
    }
  }

  private advanceCursor(): void {
    const obs = this.obstacles;
    const x = this.s.x;
    let c = this.cursor;
    while (c < obs.length && obs[c]!.x + obs[c]!.w <= x) c++;
    this.cursor = c;
  }

  /** AABB overlap against spike / bar boxes, integer compares only. */
  private hits(x: number, y: number, w: number, h: number): boolean {
    const obs = this.obstacles;
    const right = (x + w) | 0;
    for (let i = this.cursor; i < obs.length; i++) {
      const o = obs[i]!;
      if (o.x >= right) break;
      if (o.type === 'gap') continue;
      if (x >= o.x + o.w) continue;
      if (o.type === 'spike') {
        if (y < SPIKE_H && y + h > 0) return true;
      } else if (y < BAR_TOP && y + h > BAR_BOTTOM) {
        return true;
      }
    }
    return false;
  }

  /** True when the player's entire foot span sits inside a gap (§2.4). */
  private overGap(x: number, w: number): boolean {
    const obs = this.obstacles;
    for (let i = this.cursor; i < obs.length; i++) {
      const o = obs[i]!;
      if (o.x > x) break;
      if (o.type !== 'gap') continue;
      if (x >= o.x && x + w <= o.x + o.w) return true;
    }
    return false;
  }
}
